import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import FormField from '@/components/FormField'
import ModalForm from '@/components/Modal/ModalForm'
import { AsyncSelect } from '@/components/common/AsyncSelect'
import AvailabilityDayField from '@/components/common/AvailabilityDayField'
import DirtyModal from '@/components/common/DirtyModal'
import type { AvailabilityFormDay } from '@/constants/availability-form'
import { daysOfWeek } from '@/constants/days-of-week'
import useExternalErrors from '@/hooks/useExternalErrors'
import { toast } from '@/hooks/useToast'
import {
  useTimetableSetttings,
  type TimetableSettings
} from '@/modules/timetable'
import {
  useUserAvailability,
  useUserAvailabilitySemesters,
  type AvailabilitySlot,
  type UserAvailability
} from '@/modules/users'
import { usersQueryOptions } from '@/queries/usersQueryOptions'
import type { Semester } from '@/types/semester'

import styles from './UserAvailabilityModal.module.scss'
import {
  setUserAvailabilityFormSchema,
  type SetAvailabilityForm
} from '../../constants/set-availability-payload'
import useSetUserAvailability from '../../mutations/useSetUserAvailability'

const defaultValues = {
  user: undefined,
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: []
}

const parseAvailabilitySlots = (
  availabilitySlots: AvailabilitySlot[]
): AvailabilityFormDay =>
  availabilitySlots.map(({ endTime, isOptional, startTime }) => ({
    isOptional,
    time: {
      from: startTime,
      to: endTime
    }
  }))

type UserAvailabilityModalProps = {
  title?: string
  open: boolean
  onOpenChange: (isOpen: boolean) => void
  onSuccess?: () => void
  semester: Semester
  userId?: string
  userName?: string
}

const UserAvailabilityModal = (props: UserAvailabilityModalProps) => {
  const { data } = useTimetableSetttings()
  if (!data) return null
  return (
    <UserAvailabilityModalWithWorkingHours {...props} workingHours={data} />
  )
}

type UserAvailabilityModalWithWorkingHoursProps = UserAvailabilityModalProps & {
  workingHours: TimetableSettings
}

const UserAvailabilityModalWithWorkingHours = (
  props: UserAvailabilityModalWithWorkingHoursProps
) => {
  const form = useForm<SetAvailabilityForm>({
    resolver: zodResolver(setUserAvailabilityFormSchema(props.workingHours)),
    defaultValues: {
      ...defaultValues
    },
    mode: 'onBlur'
  })

  const [isDirtyModalOpen, setIsDirtyModalOpen] = useState(false)
  const isDirty = form.formState.isDirty

  const userId = props.userId ? props.userId : form.getValues('user.value')

  const { data: semesters } = useUserAvailabilitySemesters({ userId })

  const userAvailability = useUserAvailability({
    semester: props.semester.id,
    userId
  })

  const userAvailabilityFromPreviousSemester = useUserAvailability({
    semester: semesters?.previousSemester?.id,
    userId: form.getValues('user.value')
  })

  const setUserAvailability = (availability: UserAvailability) => {
    daysOfWeek.forEach(day => {
      form.setValue(day, parseAvailabilitySlots(availability[day]))
    })
  }

  const handleCopyFromPreviousSemester = () => {
    if (!userAvailabilityFromPreviousSemester.data) return

    setUserAvailability(userAvailabilityFromPreviousSemester.data)

    toast({
      variant: 'success',
      title: t('toast.successfully-copy-availability', {
        PREVIOUS_SEMESTER: semesters?.previousSemester.name
      })
    })
  }

  useEffect(() => {
    if (!userId) form.reset({ ...defaultValues })
    else if (userAvailability.data)
      form.reset({
        user: form.getValues('user'),
        monday: parseAvailabilitySlots(userAvailability.data.monday),
        tuesday: parseAvailabilitySlots(userAvailability.data.tuesday),
        wednesday: parseAvailabilitySlots(userAvailability.data.wednesday),
        thursday: parseAvailabilitySlots(userAvailability.data.thursday),
        friday: parseAvailabilitySlots(userAvailability.data.friday),
        saturday: parseAvailabilitySlots(userAvailability.data.saturday),
        sunday: parseAvailabilitySlots(userAvailability.data.sunday)
      })
  }, [userAvailability.data, form, userId, props.open])

  useEffect(() => {
    if (!props.open) form.setValue('user', null)
  }, [form, props.open])

  useEffect(() => {
    if (props.userName && props.userId)
      form.setValue('user', { label: props.userName, value: props.userId })
  }, [form, props.userId, props.userName])

  const { t } = useTranslation('availabilityManagement')

  const {
    mutate: setAvailability,
    formErrors,
    isPending
  } = useSetUserAvailability({
    onSuccess: () => {
      toast({
        title: t('toast.successfully-saved-changes'),
        variant: 'success'
      })
      props.onOpenChange(false)
      props.onSuccess?.()
      userAvailability.refetch()
    },
    onError: () => {
      toast({ title: t('toast.failed-to-save-changes'), variant: 'error' })
    }
  })

  const handleClose = () =>
    isDirty ? setIsDirtyModalOpen(true) : props.onOpenChange(false)

  useExternalErrors(formErrors, form)

  return (
    <>
      <ModalForm
        form={form}
        onSubmit={data => {
          setAvailability({
            ...data,
            semesterId: props.semester.id,
            userId
          })
        }}
        open={props.open}
        submitText={t('button.set')}
        title={
          props.title ??
          t('header.set-availability-for-semester', {
            SEMESTER: props.semester.name
          })
        }
        onCancel={handleClose}
        onClose={handleClose}
        onOpenChange={props.onOpenChange}
        size="md"
        loading={userAvailability.isLoading || isPending}
        contentClassName={styles.modal}
      >
        <div className={styles.container}>
          {props.userId ? null : (
            <FormField
              id="availability-for-user"
              label={t('label.user')}
              control={form.control}
              name="user"
              render={({ inputProps }) => (
                <AsyncSelect
                  {...inputProps}
                  value={inputProps.value ?? undefined}
                  placeholder={t('placeholder.select-user')}
                  queryOptions={search => usersQueryOptions({ search })}
                />
              )}
            />
          )}

          {semesters?.previousSemester &&
          userAvailabilityFromPreviousSemester.data ? (
            <div className={styles.buttonWrapper}>
              <Button
                variant="tertiary"
                onClick={handleCopyFromPreviousSemester}
              >
                {t('button.copy-from-previous-semester')}
              </Button>
            </div>
          ) : null}

          {userId ? (
            daysOfWeek.map(day => (
              <AvailabilityDayField
                form={form}
                key={day}
                day={day}
                appendOnSwitchOn={userAvailability.data?.[day].length === 0}
              />
            ))
          ) : (
            <span className={styles.help}>
              {t('help.select-user-to-set-availability')}
            </span>
          )}
        </div>
      </ModalForm>
      <DirtyModal
        isOpen={isDirtyModalOpen}
        onOpenChange={setIsDirtyModalOpen}
        onConfirm={() => {
          props.onOpenChange(false)
          setIsDirtyModalOpen(false)
        }}
      />
    </>
  )
}

export default UserAvailabilityModal
